import { z } from "zod";

import { AnalyticsStructuredQueryBodySchema } from "./analytics.schemas";
import { isSafeWebhookUrl } from "./is-safe-webhook-url";

// ---------------------------------------------------------------------------
// Enums (string values mirror the backend AnalyticsReport* domain enums)
// ---------------------------------------------------------------------------

/**
 * Delivery format of a scheduled report's rendered payload. CSV (Excel-friendly,
 * BOM + CRLF), JSON (machine-readable array of row objects), XLSX (native
 * spreadsheet), or PDF (human-readable table snapshot). The renderer is selected
 * per run from the stored format.
 */
export const AnalyticsReportFormatSchema = z.enum(["CSV", "JSON", "XLSX", "PDF"]);
export type AnalyticsReportFormat = z.infer<typeof AnalyticsReportFormatSchema>;

/** ACTIVE schedules fire on their cron; PAUSED ones keep their config but never run. */
export const AnalyticsReportScheduleStatusSchema = z.enum(["ACTIVE", "PAUSED"]);
export type AnalyticsReportScheduleStatus = z.infer<typeof AnalyticsReportScheduleStatusSchema>;

/** Outcome of the most recent tick, surfaced for observability. */
export const AnalyticsReportRunStatusSchema = z.enum(["SUCCESS", "FAILED"]);
export type AnalyticsReportRunStatus = z.infer<typeof AnalyticsReportRunStatusSchema>;

// ---------------------------------------------------------------------------
// Shared field schemas
// ---------------------------------------------------------------------------

/**
 * Recipient email addresses, capped so a single schedule can't fan out an
 * unbounded blast. Duplicates are harmless (each gets one email). Intentionally
 * NOT `.min(1)`: an empty array is a valid "no email recipients" state (a
 * Slack-only report, or a PATCH clearing email while a Slack target remains).
 * The "at least one delivery target" rule spans channels and is enforced by the
 * create refine and the update use-case's merged-state guard, never here.
 */
const RecipientsSchema = z.array(z.string().email()).max(50);

/**
 * Slack delivery target. `deploymentId` pins WHICH of the org's Slack
 * deployments (and therefore which bot token) posts the report — resolving by
 * type alone would be ambiguous for an org with several Slack deployments.
 * `channelId` is the destination channel (e.g. `C0123456789`); the bot must be
 * a member of it.
 */
export const AnalyticsReportSlackDeliverySchema = z.object({
  deploymentId: z.string().uuid(),
  channelId: z.string().trim().min(1).max(64)
});
export type AnalyticsReportSlackDelivery = z.infer<typeof AnalyticsReportSlackDeliverySchema>;

/**
 * Webhook delivery target. `url` must be a public http(s) endpoint carrying no
 * credential section — `isSafeWebhookUrl` is the string-decidable prefix of the
 * rule the DIAL applies, so every ground refused here is one delivery would
 * refuse anyway, and the customer learns it while they can still change the
 * field. `secret`, when set, is the HMAC-SHA256 signing key (stored encrypted at
 * rest, never returned on read — the response view exposes only whether one is
 * set) and is how this endpoint is authenticated; `user:pass@` in the URL is not.
 */
export const AnalyticsReportWebhookDeliverySchema = z.object({
  url: z.string().url().max(2048).refine(isSafeWebhookUrl, {
    message:
      "Webhook URL must be a public http(s) endpoint with no embedded credentials — loopback, private, link-local and internal hostnames, and a `user:pass@` authority, are not allowed"
  }),
  secret: z.string().min(1).max(256).optional()
});
export type AnalyticsReportWebhookDelivery = z.infer<typeof AnalyticsReportWebhookDeliverySchema>;

/**
 * Read view of a webhook target — the secret is redacted to a boolean so the
 * UI can show "signing enabled" without ever exposing the key.
 */
export const AnalyticsReportWebhookViewSchema = z.object({
  url: z.string(),
  hasSecret: z.boolean()
});
export type AnalyticsReportWebhookView = z.infer<typeof AnalyticsReportWebhookViewSchema>;

/** True when a report has at least one place to be delivered (email, Slack, or webhook). */
function hasDeliveryTarget(body: {
  recipients?: string[];
  slack?: AnalyticsReportSlackDelivery | null;
  webhook?: AnalyticsReportWebhookDelivery | null;
}): boolean {
  return (body.recipients?.length ?? 0) > 0 || body.slack != null || body.webhook != null;
}

/**
 * A cron expression is validated structurally on the backend (5 or 6 fields);
 * here we only guard against empty/oversized input so the contract stays
 * transport-agnostic and doesn't duplicate the field-level cron grammar.
 */
const CronExpressionSchema = z.string().trim().min(1).max(120);

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

/**
 * Create a scheduled analytics report. `query` is a full structured-analytics
 * request stored verbatim (self-validating against the analytics catalog) and
 * re-run on every tick — the saved query and the schedule are one entity.
 */
export const CreateAnalyticsReportScheduleBodySchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    query: AnalyticsStructuredQueryBodySchema,
    cronExpression: CronExpressionSchema,
    timezone: z.string().trim().min(1).max(64).default("UTC"),
    recipients: RecipientsSchema.optional(),
    slack: AnalyticsReportSlackDeliverySchema.optional(),
    webhook: AnalyticsReportWebhookDeliverySchema.optional(),
    format: AnalyticsReportFormatSchema.default("CSV")
  })
  .refine(hasDeliveryTarget, {
    message:
      "At least one delivery target is required: provide recipients, slack, webhook, or any combination",
    path: ["recipients"]
  });
export type CreateAnalyticsReportScheduleBody = z.infer<
  typeof CreateAnalyticsReportScheduleBodySchema
>;

/**
 * Partial update. Every field is optional; `status` pauses/resumes the
 * schedule. An empty body is rejected so a no-op PATCH is an explicit 400
 * rather than a silent success.
 */
export const UpdateAnalyticsReportScheduleBodySchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    query: AnalyticsStructuredQueryBodySchema,
    cronExpression: CronExpressionSchema,
    timezone: z.string().trim().min(1).max(64),
    recipients: RecipientsSchema,
    // Nullable so a caller can CLEAR Slack delivery (`slack: null`) distinctly
    // from leaving it untouched (field omitted). The backend rejects an update
    // that would leave the schedule with no delivery target at all.
    slack: AnalyticsReportSlackDeliverySchema.nullable(),
    // Same clear-vs-leave semantics for webhook: `null` clears it, omitted
    // leaves it, an object sets it (a set with no `secret` clears the secret).
    webhook: AnalyticsReportWebhookDeliverySchema.nullable(),
    format: AnalyticsReportFormatSchema,
    status: AnalyticsReportScheduleStatusSchema
  })
  .partial()
  .refine((body) => Object.keys(body).length > 0, {
    message: "At least one field must be provided to update"
  });
export type UpdateAnalyticsReportScheduleBody = z.infer<
  typeof UpdateAnalyticsReportScheduleBodySchema
>;

// ---------------------------------------------------------------------------
// Response view
// ---------------------------------------------------------------------------

/**
 * Public view of a report schedule. `query` is echoed back as an opaque object
 * (it was validated on write); run-tracking fields are nullable until the
 * first tick. Timestamps are ISO strings.
 */
export const AnalyticsReportScheduleSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  name: z.string(),
  query: z.record(z.string(), z.unknown()),
  cronExpression: z.string(),
  timezone: z.string(),
  recipients: z.array(z.string()),
  slack: AnalyticsReportSlackDeliverySchema.nullable(),
  // The webhook secret is NEVER returned — the view exposes only the URL and
  // whether a signing secret is configured.
  webhook: AnalyticsReportWebhookViewSchema.nullable(),
  format: AnalyticsReportFormatSchema,
  status: AnalyticsReportScheduleStatusSchema,
  lastRunAt: z.string().datetime().nullable(),
  lastRunStatus: AnalyticsReportRunStatusSchema.nullable(),
  lastError: z.string().nullable(),
  nextRunAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().nullable()
});
export type AnalyticsReportSchedule = z.infer<typeof AnalyticsReportScheduleSchema>;

/** List response payload — a flat array (schedules are few per org). */
export const ListAnalyticsReportSchedulesSchema = z.object({
  reports: z.array(AnalyticsReportScheduleSchema)
});
export type ListAnalyticsReportSchedules = z.infer<typeof ListAnalyticsReportSchedulesSchema>;

// ---------------------------------------------------------------------------
// Slack channel discovery (for the delivery-target picker)
// ---------------------------------------------------------------------------

/**
 * A Slack channel reachable by a deployment's bot — the shape the report
 * editor's channel picker consumes. `isPrivate` lets the UI badge private
 * channels; the bot must be a member of a channel to actually post there.
 */
export const AnalyticsReportSlackChannelSchema = z.object({
  id: z.string(),
  name: z.string(),
  isPrivate: z.boolean()
});
export type AnalyticsReportSlackChannel = z.infer<typeof AnalyticsReportSlackChannelSchema>;

/** Channel-list response for a given Slack deployment. */
export const ListAnalyticsReportSlackChannelsSchema = z.object({
  channels: z.array(AnalyticsReportSlackChannelSchema)
});
export type ListAnalyticsReportSlackChannels = z.infer<
  typeof ListAnalyticsReportSlackChannelsSchema
>;

// ---------------------------------------------------------------------------
// Run history
// ---------------------------------------------------------------------------

/**
 * One recorded execution of a schedule — the audit trail behind the schedule's
 * `lastRun*` fields. `rowCount` is the number of rows delivered (null when the
 * run failed before producing a result); `error` holds the failure message on a
 * FAILED run. `runAt` is an ISO string.
 */
export const AnalyticsReportRunSchema = z.object({
  id: z.string().uuid(),
  scheduleId: z.string().uuid(),
  status: AnalyticsReportRunStatusSchema,
  rowCount: z.number().int().nullable(),
  format: AnalyticsReportFormatSchema,
  error: z.string().nullable(),
  runAt: z.string().datetime()
});
export type AnalyticsReportRun = z.infer<typeof AnalyticsReportRunSchema>;

/** Run-history response — most recent first, bounded per schedule. */
export const ListAnalyticsReportRunsSchema = z.object({
  runs: z.array(AnalyticsReportRunSchema)
});
export type ListAnalyticsReportRuns = z.infer<typeof ListAnalyticsReportRunsSchema>;

// ---------------------------------------------------------------------------
// Action responses (delete / run-now acknowledgements)
// ---------------------------------------------------------------------------

/** Response for a successful schedule deletion. */
export const DeleteAnalyticsReportScheduleResponseSchema = z.object({
  id: z.string().uuid(),
  deleted: z.literal(true)
});
export type DeleteAnalyticsReportScheduleResponse = z.infer<
  typeof DeleteAnalyticsReportScheduleResponseSchema
>;

/**
 * Response for an on-demand "run now" trigger. The run is fired out of band
 * (enqueued), so this only acknowledges the enqueue — poll the run history for
 * the outcome.
 */
export const RunAnalyticsReportNowResponseSchema = z.object({
  scheduleId: z.string().uuid(),
  enqueued: z.literal(true)
});
export type RunAnalyticsReportNowResponse = z.infer<typeof RunAnalyticsReportNowResponseSchema>;
